import { useEffect, useMemo, useRef, useState } from "react";
import type { ProductDto } from "../api/products";
import "../styles/productSelect.css"

interface ProductSelectV2Props {
  products: ProductDto[];
  onSelect: (product: ProductDto) => void;
}

export const ProductSelectV2 = ({ products, onSelect }: ProductSelectV2Props) => {
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef<HTMLDivElement | null>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];

    return products.filter((p) => {
      const name = p.nameRu?.toLowerCase() ?? "";
      const sku = (p as any).sku?.toLowerCase?.() ?? "";
      const barcode = (p as any).barcode?.toLowerCase?.() ?? "";

      return (
        name.includes(q) ||
        sku.includes(q) ||
        barcode.includes(q)
      );
    });
  }, [products, query]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  useEffect(() => {
    if (!listRef.current) return;
    const items = listRef.current.querySelectorAll<HTMLDivElement>(".product-select-item");
    if (items.length === 0) return;
    const el = items[activeIndex];
    if (el) {
      el.scrollIntoView({ block: "nearest" });
    }
  }, [activeIndex, filtered.length]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (filtered.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) => (prev + 1) % filtered.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) => (prev - 1 + filtered.length) % filtered.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const p = filtered[activeIndex];
      if (p) {
        onSelect(p);
        setQuery("");
      }
    } else if (e.key === "Escape") {
      setQuery("");
    }
  };

  const highlight = (text: string) => {
    const q = query.trim();
    if (!q) return text;
    const idx = text.toLowerCase().indexOf(q.toLowerCase());
    if (idx === -1) return text;

    const before = text.slice(0, idx);
    const match = text.slice(idx, idx + q.length);
    const after = text.slice(idx + q.length);

    return (
      <>
        {before}
        <mark>{match}</mark>
        {after}
      </>
    );
  };

  return (
    <div className="product-select">
      <input
        className="product-select-input"
        placeholder="Поиск товара (имя / SKU / штрих‑код)"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={handleKeyDown}
      />

      {filtered.length > 0 && (
        <div className="product-select-list" ref={listRef}>
          {filtered.map((p, index) => (
            <div
              key={p.id}
              className={
                "product-select-item" +
                (index === activeIndex ? " active" : "")
              }
              onMouseDown={(e) => {
                e.preventDefault();
                onSelect(p);
                setQuery("");
              }}
            >
              <div className="product-select-name">
                {highlight(p.nameRu ?? `Товар #${p.id}`)}
              </div>
              <div className="product-select-meta">
                SKU: {(p as any).sku || "—"} | ШК: {(p as any).barcode || "—"} | {p.salePrice} ₸
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
